import type { ReviewerDto } from '../dto/vendor-review.dto.js'

/**
 * How reviews read wherever they show: the app, the public site and email
 * (docs/vendor-growth.spec.md, "Reviews"). One place, so the writer is shown
 * exactly what everyone else will read.
 */

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

/**
 * "Jamie R.": a person's first word and the first letter of their last. A
 * single word stays as it is, and a blank name reads "A couple": only couples
 * are shown this way, since a planner shows as its company.
 */
export function reviewerShortName(fullName: string): string {
  const words = fullName.trim().split(/\s+/).filter((word) => word !== '')
  const first = words[0]
  const last = words.at(-1)
  if (first === undefined || last === undefined) return 'A couple'
  if (words.length === 1) return first
  return `${first} ${last.charAt(0).toLocaleUpperCase('en-US')}.`
}

/** "2026-10", from a calendar day such as "2026-10-24". */
export function reviewMonthOf(day: string): string {
  return day.slice(0, 7)
}

/** "October 2026", from "2026-10". */
export function reviewMonthLabel(month: string): string {
  const [year, number] = month.split('-')
  const name = MONTHS[Number(number) - 1]
  return name === undefined || year === undefined ? month : `${name} ${year}`
}

const ROLE_LABELS: Record<ReviewerDto['role'], string> = { couple: 'Couple', planner: 'Planner' }

/** "Jamie R. · Couple · October 2026". */
export function reviewerLine(reviewer: ReviewerDto, month: string): string {
  return `${reviewer.name} · ${ROLE_LABELS[reviewer.role]} · ${reviewMonthLabel(month)}`
}

/** "★★★★☆". Shown with `ratingLabel` for anyone not reading the stars. */
export function ratingStars(rating: number): string {
  const filled = Math.max(0, Math.min(5, Math.round(rating)))
  return '★'.repeat(filled) + '☆'.repeat(5 - filled)
}

/** "4 out of 5 stars", "1 out of 5 stars". */
export function ratingLabel(rating: number): string {
  return `${String(rating)} out of 5 stars`
}

/** "Showing the 20 most recent of 23 reviews.": said when a page lists fewer than there are. */
export function reviewsShownLabel(shown: number, count: number): string {
  return `Showing the ${String(shown)} most recent of ${String(count)} reviews.`
}

/** "4.8 out of 5 · 23 reviews", "5.0 out of 5 · 1 review". */
export function reviewSummaryLabel(summary: { average: number; count: number }): string {
  return `${summary.average.toFixed(1)} out of 5 · ${String(summary.count)} ${summary.count === 1 ? 'review' : 'reviews'}`
}
